import {
  DASHBOARD_APPEARANCE_COOKIE,
  type DashboardAppearance,
  dashboardAppearanceOrDefault,
  type ResolvedDashboardAppearance,
  readDashboardAppearanceCookie,
} from "@playbrain/config/dashboardAppearance"
import { createIsomorphicFn } from "@tanstack/react-start"
import { getCookie } from "@tanstack/react-start/server"

/**
 * The appearance to render `<html>` with, read the same way on both sides.
 *
 * The root route runs on the server for the first request and in the browser
 * for every navigation after it, so this has to answer in both places. On the
 * server that is the request's `Cookie` header; in the browser it is
 * `document.cookie`. Either way it is a synchronous read of a string — cheap
 * enough to do in a root loader on every route.
 *
 * The MySQL column remains authoritative. This cookie is a same-response
 * mirror of it, written whenever the column is written, because the column is
 * only readable after the session resolves and the class has to be on the
 * element before that.
 */
export const readDashboardAppearance = createIsomorphicFn()
  .server(
    (): DashboardAppearance =>
      // Synchronous: `getCookie` reads the request this render is already for.
      // The Start plugin strips this branch — and its server-only import — from
      // the client bundle, which is why importing it at the top level is safe.
      dashboardAppearanceOrDefault(getCookie(DASHBOARD_APPEARANCE_COOKIE))
  )
  .client((): DashboardAppearance => readDashboardAppearanceCookie(document.cookie))

/**
 * Resolves "system" against the request.
 *
 * A server has no way to know what a device's colour scheme is — the
 * `Sec-CH-Prefers-Color-Scheme` hint exists but is not sent unless requested,
 * and requesting it costs a round trip. So the server renders a "system" user
 * as dark and the client corrects it on hydration if the device disagrees.
 *
 * That correction is a class swap on an element that is already painted, not
 * a re-render, and it only happens for people who chose "system" *and* are on
 * a light device. Explicit light and explicit dark — which is everyone by
 * default — never flash.
 */
export function resolveServerAppearance(preference: DashboardAppearance): ResolvedDashboardAppearance {
  return preference === "light" ? "light" : "dark"
}
